import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Container,
  useTheme,
  IconButton,
  Tooltip,
  InputAdornment, // 💡 ADDED for logo helper text/icon
} from "@mui/material";
import { useNavigate } from "react-router-dom";
// KEEPING YOUR ORIGINAL BACKEND IMPORTS:
import { fetchDataFromAPI, saveData } from "../components/api.js";
import subscriptionStatus from "../components/SubscriptionStatus.js";
import { useTheme as useAppTheme } from "../context/ThemeContext.js";
import useCurrencies from "../components/useCurrencies.js";
import useTimeZone from "../components/useTimeZones.js";
import useLanguages from "../components/useLanguages.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import countries from "../data/countries";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import config from "../config";
import { useParams } from "react-router-dom";
const baseUrl = config.baseURL;
import WallpaperIcon from "@mui/icons-material/Wallpaper"; // 💡 Icon for Logo field
import CloseIcon from "@mui/icons-material/Close"; // Import an icon for better styling
import {
  companyTypes,
  businessCategories,
  businessIndustries,
} from "../data/dropdownOptions";
// --- Color Constants (Unchanged) ---
const BACKGROUND_COLOR = "#D1D8E0";
const PRIMARY_TEXT_COLOR = "#2E4053";
const ACCENT_COLOR = "#34495E";

// --- Initial State Definitions (Updated) ---
const initialUserData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  countryCode: "",
};
const initialCompanyData = {
  companyName: "",
  companyType: "",
  websiteUrl: "",
  description: "",
  tagline: "",
  companySize: "",
  currency: "",
  timeZone: "",
  language: "",
  business_category: "",
  business_industry: "",
};

// --- Utility: parseJwt (Unchanged) ---
const parseJwt = (token) => {
  if (!token) return null;
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );
  return JSON.parse(jsonPayload);
};

// --- Main Component ---
const EditProfile = () => {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const { theme, isSubscribed } = useAppTheme();
  const isSubscribedStatus = subscriptionStatus();

  const [userData, setUserData] = useState(initialUserData);
  const [companyData, setCompanyData] = useState(initialCompanyData);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const { currencies } = useCurrencies();
  const { languages, error: languageError } = useLanguages();
  const { timeZones } = useTimeZone();

  // --- Data Fetching Logic (Updated for new fields) ---
  const fetchProfileData = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setFormError(null);
    try {
      const decoded = parseJwt(token);
      const res = await fetchDataFromAPI(`/api/profile`, {}, token);
      const response = res.data;

      console.log("res", res);
      console.log("response", response.firstName);
      if (response) {
        setUserData({
          firstName: response.firstName || "",
          lastName: response.lastName || "",
          email: response.email || "",
          phone: response.phone || "",
          countryCode: response.countryCode || "", // 💡 Mapped
        });
        setCompanyData({
          companyName: response.companyName || "",
          companyType: response.companyType || "",
          websiteUrl: response.websiteUrl || "",
          description: response.description || "",
          tagline: response.tagline || "",
          companySize: response.companySize || "",
          currency: response.currency || "",
          timeZone: response.timeZone || "",
          language: response.language || "",
          business_category: response.business_category || "", // 💡 Mapped
          business_industry: response.business_industry || "", // 💡 Mapped
          logo: response.logo || null, // 💡 Mapped
        });
      }
    } catch (err) {
      console.error("Error fetching profile data:", err);
      setFormError("Failed to load profile data. Please try refreshing.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  // --- Change Handlers (Unchanged) ---
  const handleFieldChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name in initialUserData) {
      setUserData((prevData) => ({ ...prevData, [name]: value }));
    } else if (name in initialCompanyData) {
      setCompanyData((prevData) => ({ ...prevData, [name]: value }));
    }
    setFormError(null);
  }, []);

  const handleContactClick = () => {
    navigate("/dashboard/CustomerSupport");
  };

  // --- Submission Logic (Unchanged) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setFormError(null);
    if (!userData.firstName || !userData.lastName) {
      setFormError("First Name and Last Name are required.");
      setIsSaving(false);
      return;
    }
    const updatedData = { ...userData, ...companyData };
    const token = localStorage.getItem("token");
    const decoded = parseJwt(token);
    console.log("updatedData", updatedData);
    const userId = decoded?.userId;

    if (!userId) {
      setFormError(
        "Authentication error: User ID not found. Please log in again."
      );
      setIsSaving(false);
      return;
    }
    try {
      await saveData(`/api/profile/edit/${userId}`, updatedData, "PUT");
      toast.success("Profile configuration successfully updated!", {
        position: "top-right",
      });
      await fetchProfileData();
    } catch (err) {
      console.error("Error updating profile:", err);
      setFormError(
        "An unexpected error occurred while saving. Please check your data."
      );
      toast.error("Failed to save profile changes.", {
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Render Logic ---
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          p: 4,
          backgroundColor: BACKGROUND_COLOR,
        }}
      >
        <CircularProgress sx={{ color: PRIMARY_TEXT_COLOR }} />
        <Typography sx={{ ml: 2, color: PRIMARY_TEXT_COLOR }}>
          Loading profile configuration...
        </Typography>
      </Box>
    );
  }
  // --- Inside EditProfile component ---
  const handleDeleteLogo = async () => {
    try {
      const token = localStorage.getItem("token");
      const decoded = parseJwt(token);
      const userId = decoded?.userId;

      await axios.delete(`${baseUrl}/api/profile/delete/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCompanyData((prev) => ({ ...prev, logo: null }));
      toast.success("Logo deleted successfully!");
    } catch (err) {
      console.error("Delete logo error:", err.response?.data || err.message);
      toast.error("Failed to delete logo.");
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    try {
      // Show local preview immediately
      setCompanyData((prev) => ({ ...prev, logo: file }));
      const formData = new FormData();
      formData.append("logo", file);
      const token = localStorage.getItem("token");
      const decoded = parseJwt(token);
      const userId = decoded?.userId;
      console.log("decoded", decoded);
      // Changed from axios.post to axios.put to match the route
      const response = await axios.post(
        `${baseUrl}/api/profile-edit/logo/${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (!response || !response.data) {
        throw new Error("Failed to upload logo");
      }
      // If backend returns the saved file URL, use it
      const savedLogoUrl = response.data.data?.logo; // Added optional chaining for safety
      setCompanyData((prev) => ({
        ...prev,
        logo: savedLogoUrl || file, // Fallback to local file if URL not returned
      }));
      toast.success("Logo uploaded successfully!");
    } catch (err) {
      console.error("Logo upload failed:", err.response?.data || err.message);
      toast.error("Failed to upload logo.");
      // Optional: Reset logo on failure to avoid inconsistent state
      setCompanyData((prev) => ({ ...prev, logo: null }));
    }
  };

  return (
    <Container
      component="main"
      // maxWidth="md"
      sx={{
        py: 4,
        backgroundColor: BACKGROUND_COLOR,
        minHeight: "100vh",
        py: 4,
        // Use a slight bottom padding to lift the content from the bottom edge
        pb: 8,
      }}
    >
      <Paper
        elevation={4}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            fontWeight: 600,
            color: PRIMARY_TEXT_COLOR,
          }}
        >
          Manage User & Organization Profile ⚙️
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          sx={{
            mb: 4,
            color: ACCENT_COLOR,
          }}
        >
          Review and update your core identity and company settings.
        </Typography>
        <ToastContainer />
        {formError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {formError}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          {/* USER INFORMATION SECTION */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{ mt: 2, color: ACCENT_COLOR }}
          >
            User Details
          </Typography>
          <Divider sx={{ mb: 3, bgcolor: ACCENT_COLOR }} />
          <Grid container spacing={3}>
            {/* Row 1: First Name / Last Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name (Required)"
                name="firstName"
                variant="outlined"
                value={userData.firstName}
                onChange={handleFieldChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name (Required)"
                name="lastName"
                variant="outlined"
                value={userData.lastName}
                onChange={handleFieldChange}
                required
              />
            </Grid>

            {/* Row 2: Email (Read-only) / Contact Support Button */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Primary Email"
                name="email"
                variant="filled"
                value={userData.email}
                InputProps={{ readOnly: true }}
                helperText="To modify your primary email, please contact customer support."
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Button
                variant="outlined"
                onClick={handleContactClick}
                fullWidth
                sx={{
                  height: "56px",
                  color: ACCENT_COLOR,
                  borderColor: ACCENT_COLOR,
                  "&:hover": {
                    borderColor: PRIMARY_TEXT_COLOR,
                    backgroundColor: "rgba(52, 73, 94, 0.04)",
                  },
                }}
              >
                Request Email Change
              </Button>
            </Grid>

            {/* Row 3: Phone Number and Country Code (Properly Grouped) */}

            <Divider sx={{ mb: 3, bgcolor: ACCENT_COLOR }} />
            <Grid container spacing={3}>
              {/* 👇 START OF CORRECT PHONE / COUNTRY CODE ROW 👇 */}

              {/* Country Code (4 columns wide on medium screens) */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Country Code</InputLabel>
                  <Select
                    name="countryCode"
                    value={userData.countryCode || ""}
                    onChange={handleFieldChange}
                    label="Country Code"
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.dialCode} value={country.dialCode}>
                        <Tooltip title={country.name} placement="right">
                          <span>
                            {country.dialCode} ({country.flag})
                          </span>
                        </Tooltip>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Phone Number (8 columns wide on medium screens, placed next to the 4-column item) */}
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  variant="outlined"
                  value={userData.phone}
                  onChange={handleFieldChange}
                  type="tel"
                />
              </Grid>

              {/* 👆 END OF CORRECT PHONE / COUNTRY CODE ROW 👆 */}
            </Grid>
          </Grid>

          {/* ORGANIZATION INFORMATION SECTION */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{ mt: 5, color: ACCENT_COLOR }}
          >
            Organization Settings
          </Typography>
          <Divider sx={{ mb: 3, bgcolor: ACCENT_COLOR }} />
          <Grid container spacing={3}>
            {/* Row 1: Company Name / Company Type */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Name"
                name="companyName"
                variant="outlined"
                value={companyData.companyName}
                onChange={handleFieldChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Company Type</InputLabel>
                <Select
                  name="companyType"
                  value={companyData.companyType || ""}
                  onChange={handleFieldChange}
                  label="Company Type"
                >
                  {companyTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Row 2: Business Category / Business Industry (New Fields) */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Business Category</InputLabel>
                <Select
                  name="business_category"
                  value={companyData.business_category || ""}
                  onChange={handleFieldChange}
                  label="Business Category"
                >
                  {businessCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Business Industry</InputLabel>
                <Select
                  name="business_industry"
                  value={companyData.business_industry || ""}
                  onChange={handleFieldChange}
                  label="Business Industry"
                >
                  {businessIndustries.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Row 3: Website URL */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website URL"
                name="websiteUrl"
                variant="outlined"
                value={companyData.websiteUrl}
                onChange={handleFieldChange}
                type="url"
              />
            </Grid>
            {/* Row 4: Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Description (Mission/Vision)"
                name="description"
                variant="outlined"
                value={companyData.description}
                onChange={handleFieldChange}
                multiline
                rows={3}
              />
            </Grid>
            {/* Row 5: Tagline / Company Size */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Brand Tagline"
                name="tagline"
                variant="outlined"
                value={companyData.tagline}
                onChange={handleFieldChange}
              />
            </Grid>

            {/* Row 6: Company Size / Logo Placeholder (Updated to span two rows) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company Size (Employees)"
                name="companySize"
                variant="outlined"
                value={companyData.companySize}
                onChange={handleFieldChange}
                type="number"
              />
            </Grid>
            {/* Logo Placeholder - Styled and fixed height to visually align with Company Size and Currency */}
            {/* Row 6: Logo Placeholder / Currency */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  // The box height is now more flexible, minHeight is set for the empty state
                  minHeight: "56px",
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  // Increased padding for better look
                  px: 2,
                  py: 1,
                  position: "relative",
                  backgroundColor: "#fafafa",
                }}
              >
                {/* 1. Logo Preview and Remove State */}
                {companyData.logo ? (
                  <>
                    {/* Logo Preview Container */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minWidth: 0,
                        flexGrow: 1,
                      }}
                    >
                      <img
                        src={
                          // Use the URL string directly if it's a string (e.g., from an API)
                          typeof companyData.logo === "string"
                            ? companyData.logo
                            : // Create object URL for File if it's a File object (e.g., just uploaded)
                              URL.createObjectURL(companyData.logo)
                        }
                        alt="Company Logo Preview"
                        style={{
                          maxHeight: "40px",
                          maxWidth: "100%", // Ensures it scales down if too wide
                          objectFit: "contain",
                          marginRight: 12, // Increased spacing
                          borderRadius: 4, // Slight rounding for style
                        }}
                      />
                      {/* Optional: Add file name or status if needed */}
                    </Box>

                    {/* 2. Styled Remove Button (using IconButton for cleaner look) */}
                    <IconButton
                      aria-label="Remove Logo"
                      size="small"
                      onClick={handleDeleteLogo}
                      sx={{
                        // Use an accent color for visibility, red on hover
                        color: ACCENT_COLOR,
                        "&:hover": {
                          color: "red",
                          backgroundColor: "rgba(255, 0, 0, 0.1)", // subtle hover background
                        },
                        marginLeft: 1, // Space it slightly from the image container
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  /* 3. Empty/Upload State */
                  <>
                    <Typography
                      variant="body1"
                      sx={{ color: PRIMARY_TEXT_COLOR, opacity: 0.7 }}
                    >
                      Select Company Logo
                    </Typography>
                    <Button
                      variant="contained"
                      component="label" // Crucial for making the button trigger the hidden input
                      size="small"
                      sx={{
                        backgroundColor: ACCENT_COLOR,
                        "&:hover": { backgroundColor: PRIMARY_TEXT_COLOR },
                      }}
                    >
                      Upload File
                      {/* Hidden file input */}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          // Assuming handleLogoUpload updates companyData.logo
                          await handleLogoUpload(file);
                          // Clear the input value to allow the same file to be selected again
                          e.target.value = "";
                        }}
                      />
                    </Button>
                  </>
                )}
              </Box>
            </Grid>

            {/* Row 6 continuation: Primary Currency (placed next to Logo, forming a single row) */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Primary Currency</InputLabel>
                <Select
                  name="currency"
                  value={companyData.currency || ""}
                  onChange={handleFieldChange}
                  label="Primary Currency"
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency.code} value={currency.code}>
                      {currency.label} ({currency.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* Row 7: Time Zone / Language */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Default Time Zone</InputLabel>
                <Select
                  name="timeZone"
                  value={companyData.timeZone || ""}
                  onChange={handleFieldChange}
                  label="Default Time Zone"
                >
                  {timeZones.map((zone) => (
                    <MenuItem key={zone} value={zone}>
                      {zone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>System Language</InputLabel>
                <Select
                  name="language"
                  value={companyData.language || ""}
                  onChange={handleFieldChange}
                  label="System Language"
                >
                  {languageError ? (
                    <MenuItem disabled>Error loading languages</MenuItem>
                  ) : (
                    languages.map((language, index) => (
                      <MenuItem key={index} value={language}>
                        {language}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Submission Action */}
          <Box sx={{ marginTop: 5 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={isSaving}
              startIcon={
                isSaving ? <CircularProgress size={20} color="inherit" /> : null
              }
              sx={{
                padding: muiTheme.spacing(1.5),
                fontSize: "16px",
                backgroundColor: ACCENT_COLOR,
                "&:hover": {
                  backgroundColor: PRIMARY_TEXT_COLOR,
                },
              }}
            >
              {isSaving ? "Saving Configuration..." : "Save Changes"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EditProfile;
